import type { TextStyle, ViewStyle } from 'react-native';

import { font, sizes, spacing } from '../constants';
import { ThemedStyleSheet } from '../theme';

export type BadgeKind = keyof typeof BADGE_KIND_STYLES;
export type BadgeSize = 'xsmall' | 'small' | 'medium' | 'large';

export const BADGE_ICON_SIZE: { [key in BadgeSize]: number } = {
    xsmall: sizes.ICON_100,
    small: sizes.ICON_100,
    medium: sizes.ICON_300,
    large: sizes.ICON_400,
};

export const BADGE_TEXT_SIZE: { [key in BadgeSize]: TextStyle } = {
    xsmall: font.MobileCaption,
    small: font.Caption,
    medium: font.Caption,
    large: font.Label,
};

export const BADGE_ICON_SPACING: { [key in BadgeSize]: ViewStyle } = {
    xsmall: spacing.Margin.left100,
    small: spacing.Margin.left100,
    medium: spacing.Margin.left100,
    large: spacing.Margin.left200,
};

export const BADGE_V_PADDING: { [key in BadgeSize]: number } = {
    xsmall: spacing.xxsmall / 2,
    small: spacing.xxsmall,
    medium: spacing.xxsmall,
    large: spacing.small,
};

export const BADGE_LABEL_H_PADDING: { [key in BadgeSize]: number } = {
    xsmall: spacing.xxsmall,
    small: spacing.xxsmall,
    medium: spacing.small,
    large: spacing.small + spacing.xxsmall,
};

/**
 * Sizing for each badge size.
 */
export const BADGE_HEIGHT: { [key in BadgeSize]: number } = {
    xsmall: BADGE_ICON_SIZE.xsmall + 2 * (BADGE_V_PADDING.xsmall + 1),
    small: BADGE_ICON_SIZE.small + 2 * (BADGE_V_PADDING.small + 1),
    medium: BADGE_ICON_SIZE.medium + 2 * (BADGE_V_PADDING.medium + 1),
    large: BADGE_ICON_SIZE.large + 2 * (BADGE_V_PADDING.large + 1),
};

/**
 * Colors for each badge kind.
 */
export const BADGE_KIND_STYLES = {
    secondary: ThemedStyleSheet.create({
        wrapper: (theme) => ({
            backgroundColor: theme.colors.background.main,
            borderColor: theme.colors.border.main,
        }),
        text: (theme) => ({
            color: theme.colors.text.mid,
        }),
    }),
    primary: ThemedStyleSheet.create({
        wrapper: (theme) => ({
            backgroundColor: theme.colors.primary.main,
            borderColor: theme.colors.primary.main,
        }),
        text: (theme) => ({
            color: theme.colors.info.textInside,
        }),
    }),
    info: ThemedStyleSheet.create({
        wrapper: (theme) => ({
            backgroundColor: theme.colors.info.muted,
            borderColor: theme.colors.info.muted,
        }),
        text: (theme) => ({
            color: theme.colors.info.standout,
        }),
    }),
    danger: ThemedStyleSheet.create({
        wrapper: (theme) => ({
            backgroundColor: theme.colors.danger.muted,
            borderColor: theme.colors.danger.muted,
        }),
        text: (theme) => ({
            color: theme.colors.danger.standout,
        }),
    }),
    notification: ThemedStyleSheet.create({
        wrapper: (theme) => ({
            backgroundColor: theme.colors.danger.main,
            borderColor: theme.colors.danger.main,
        }),
        text: (theme) => ({
            color: theme.colors.danger.textInside,
        }),
    }),
    success: ThemedStyleSheet.create({
        wrapper: (theme) => ({
            backgroundColor: theme.colors.success.muted,
            borderColor: theme.colors.success.muted,
        }),
        text: (theme) => ({
            color: theme.colors.success.standout,
        }),
    }),
    warning: ThemedStyleSheet.create({
        wrapper: (theme) => ({
            backgroundColor: theme.colors.warning.muted,
            borderColor: theme.colors.warning.muted,
        }),
        text: (theme) => ({
            color: theme.colors.warning.standout,
        }),
    }),
    merge: ThemedStyleSheet.create({
        wrapper: (theme) => ({
            backgroundColor: theme.colors.merge.muted,
            borderColor: theme.colors.merge.muted,
        }),
        text: (theme) => ({
            color: theme.colors.merge.standout,
        }),
    }),
    upgrade: ThemedStyleSheet.create({
        wrapper: (theme) => ({
            backgroundColor: theme.colors.upgrade.muted,
            borderColor: theme.colors.upgrade.muted,
        }),
        text: (theme) => ({
            color: theme.colors.upgrade.standout,
        }),
    }),
};
